/**
 * Cross-account spreadsheet sharing.
 *
 * Why this exists:
 *   The OAuth `drive.file` scope ONLY grants access to files this client
 *   created or that the user explicitly opened via picker. A second user
 *   (e.g. spouse) cannot see the owner's `Mochia Data` workbook even after
 *   the owner shares it via Drive UI — it's invisible to `driveFilesList`.
 *
 *   We work around this without expanding scopes: the owner generates a
 *   join URL (`?join=<spreadsheetId>`); the second user opens the URL,
 *   we persist the override id, and bootstrap skips the Drive search and
 *   uses the explicit id directly. The `spreadsheets` scope itself reads
 *   the file by id with no Drive listing required, so as long as the
 *   owner has shared the file with the second user's email (write
 *   permission), reads + writes work normally.
 *
 * Storage: localStorage (per-origin, survives reload). Reset via
 * `clearSharedSpreadsheetId()` (called from logout).
 *
 * Sharp edges:
 *   - We do NOT validate the spreadsheet id format here — the bootstrap
 *     will fail loudly via the Sheets API if it's bogus. Validating
 *     client-side would invite false-positive rejections of legitimate
 *     ids (Google's id format isn't formally documented).
 *   - The join URL is consumed once: we strip the `?join=` query after
 *     persisting so a refresh doesn't re-trigger the join flow.
 */

const STORAGE_KEY = 'mochia.sharedSpreadsheetId.v1';

export function getSharedSpreadsheetId(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

export function setSharedSpreadsheetId(id: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* private mode / quota — non-fatal */
  }
}

export function clearSharedSpreadsheetId(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* non-fatal */
  }
}

/**
 * If the URL has `?join=<id>`, persist the id to localStorage and strip the
 * query so reload doesn't reapply. Returns the id when one was consumed
 * (caller can show a toast etc.).
 */
export function consumeJoinUrlIfPresent(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    const id = url.searchParams.get('join');
    if (!id) return null;
    setSharedSpreadsheetId(id);
    url.searchParams.delete('join');
    window.history.replaceState({}, '', url.toString());
    return id;
  } catch {
    return null;
  }
}

/** Build the join URL the owner copies to share with another user. */
export function buildJoinUrl(spreadsheetId: string): string {
  if (typeof window === 'undefined') return '';
  const url = new URL(window.location.origin + '/');
  url.searchParams.set('join', spreadsheetId);
  return url.toString();
}
